/**
 * Carte d'affichage d'un éditeur
 *
 * Principe SOLID: Single Responsibility
 * - Responsable uniquement de l'affichage d'un éditeur
 *
 * @module EditeurCard
 */

import Image from 'next/image';
import { useMemo } from 'react';

import { computeEditeurPhoto } from '@/components/ui/editeurs/utils/computeEditeurPhoto';
import { IMAGES_ASSETS_PREFIX } from '@/constants';
import { IMAGE_QUALITY_ENTITY_CARD } from '@/constants/image-quality';
import { Link } from '@/i18n/navigation';
import type { Meilisearch_Editeur } from '@/lib/api/meilisearch-types';

// ============================================================================
// Types
// ============================================================================

export interface EditeurCardProps {
  /**
   * Données de l'éditeur
   */
  editeur: Meilisearch_Editeur;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Carte d'affichage d'un éditeur
 *
 * @example
 * ```tsx
 * <EditeurCard editeur={editeurData} />
 * ```
 */
export function EditeurCard({ editeur }: EditeurCardProps) {
  const photo = useMemo(() => computeEditeurPhoto(editeur), [editeur]);
  const name =
    editeur.appellation_complete ??
    [editeur.prenom, editeur.nom].filter(Boolean).join(' ') ??
    '';

  return (
    <Link
      href={{
        pathname: `/editeurs/[slug]`,
        params: { slug: editeur.slug },
      }}
      className="group card card-compact relative size-full overflow-hidden rounded-md bg-base-100 shadow-sm outline-none transition-all hover:bg-secondary-50 focus:outline-none focus-visible:outline-none active:scale-[.97]"
    >
      <figure className="relative aspect-[1/1] transition-all sm:group-hover:scale-105">
        {photo ? (
          <Image
            src={`${IMAGES_ASSETS_PREFIX}/${photo}?fit=cover&width=500&height=500&quality=${IMAGE_QUALITY_ENTITY_CARD}`}
            alt={name}
            fill
            quality={IMAGE_QUALITY_ENTITY_CARD}
            sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 33vw, 20vw"
            style={{
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-gray-200" />
        )}
      </figure>
      <div className="inset-x-0 bottom-0 z-[1] flex h-16 place-content-center items-center bg-white px-3 text-center font-gothic text-gray-700">
        <h5 className="text-center font-gothicb text-sm leading-tight">
          {name}
        </h5>
      </div>
    </Link>
  );
}
